import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';

/**
 * The mail traffic of one request, as the dashboard shows it.
 *
 * Assembled on read rather than stored: every fact in here already lives on the notifications
 * (their deliveries, their answers, their readings) and on the reply mails themselves. A second
 * copy would be one more thing that can drift out of step with what actually happened.
 */

/** What one answer inside a reply was understood to mean, and what it did. */
@Entity()
export class CorrespondenceAnswer extends AdornisEntity {
  static override _class = 'CorrespondenceAnswer';

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() }) _id!: string;
  @Field(() => String) questionTitle!: string;
  @Field(() => String) positionNumber: Maybe<string>;
  /** The passage of the mail the reading rests on. */
  @Field(() => String) quote: Maybe<string>;
  /** Set when the writer picked an offered answer — then no model was involved at all. */
  @Field(() => String) chosenOption: Maybe<string>;
  @Field(() => String) understoodAs: Maybe<string>;
  @Field(() => String) reasoning: Maybe<string>;
  @Field(() => Float) confidence: Maybe<number>;
  /** What it changed, in one line — or that it changed nothing. */
  @Field(() => String) outcome: Maybe<string>;
  /** Why parts of it were refused. Empty when everything went through. */
  @Field(() => [String]) rejections: Maybe<string[]>;
  /** `Offen` / `Beantwortet` / … — the state the question ended up in. */
  @Field(() => String) status: Maybe<string>;
  @Field(() => Boolean) needsConfirmation: Maybe<boolean>;

  constructor(doc: PartialEntityData<CorrespondenceAnswer>) {
    super(doc);
  }
}

@Entity()
export class CorrespondenceEntry extends AdornisEntity {
  static override _class = 'CorrespondenceEntry';

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() }) _id!: string;
  /** `out` = the platform asked, `in` = a person answered. */
  @Field(() => String) direction!: string;
  @Field(() => Date) at: Maybe<Date>;
  @Field(() => String) subject: Maybe<string>;
  /** The other end of the exchange — who it went to, or who it came from. */
  @Field(() => String) counterpart: Maybe<string>;
  /** Outgoing: the questions the mail carried, numbered as the recipient saw them. */
  @Field(() => [String]) questions: Maybe<string[]>;
  /** Incoming: what the person actually wrote, quoting already stripped. */
  @Field(() => String) bodyText: Maybe<string>;
  @Field(() => [CorrespondenceAnswer]) answers: Maybe<CorrespondenceAnswer[]>;
  /** Incoming: sentences that answered none of the questions. */
  @Field(() => [String]) unmatched: Maybe<string[]>;
  /** Incoming: the letter arrived but nothing came of it — offer to read it again. */
  @Field(() => Boolean) unread: Maybe<boolean>;
  /** Incoming: the reading is running right now. Mutually exclusive with `unread`. */
  @Field(() => Boolean) reading: Maybe<boolean>;
  /** Incoming: the mail itself, so it can be handed back to the reading step. */
  @Field(() => String) replyMailID: Maybe<string>;

  constructor(doc: PartialEntityData<CorrespondenceEntry>) {
    super(doc);
  }
}
